import { Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { Types } from '../../../domain/types.domain';
import { UseAuthorizationCodeInput } from '../../../domain/auth/dtos/use-authorization-code.input';
import { UseAuthorizationCodeOutput } from '../../../domain/auth/dtos/use-authorization-code.output';

import { AppHandler } from '../../app.handler';
import { AuthRepositoryInstruction } from '../../../builder/instructions/repositories/auth.repository.instructions';
import { UseAuthorizationCodeServiceInstruction } from '../../../builder/instructions/services/use-authorization-code.service.instruction';

@Component({
    selector: 'app-login-by-authorization-code',
    standalone: true,
    imports: [],
    providers: [
        new AuthRepositoryInstruction(),
        new UseAuthorizationCodeServiceInstruction(),
    ],
    templateUrl: './login-by-authorization-code.component.html',
    styleUrl: './login-by-authorization-code.component.scss',
})
export class LoginByAuthorizationCodeComponent {
    private readonly _router = inject(Router);
    private readonly _appHandler = inject(AppHandler);

    private readonly _useAuthorizationCodeService = inject(
        Types.UseAuthorizationCodeService
    );

    public routeState = this._router.getCurrentNavigation()?.extras?.state ?? {};
    public authorizationCode = signal('');
    public userId = signal('');
    public customUrlRedirect = signal('');
    public cityIdentity = signal('');

    ngOnInit(): void {
        this._appHandler.setBackButtonHome(false);
        this._appHandler.setLoading(true, {
            title: 'Processando autorização...',
            description: 'Estamos validando suas credenciais.',
        });

        this.extractStateParameters();
    }

    private extractStateParameters(): void {
        const state = this.routeState as {
            authorizationCode: string;
            userId: string;
            customUrlRedirect: string;
            cityIdentity?: string;
        };

        this.authorizationCode.set(state?.authorizationCode || '');
        this.userId.set(state?.userId || '');
        this.customUrlRedirect.set(state?.customUrlRedirect || '');
        this.cityIdentity.set(state?.cityIdentity || '');

        if (!this.authorizationCode() || !this.userId()) {
            this.handleError();
            return;
        }

        this.processAuthorizationCode();
    }

    private async processAuthorizationCode(): Promise<void> {
        try {
            const input: UseAuthorizationCodeInput = {
                authorizationCode: this.authorizationCode(),
                userId: this.userId(),
                customUrlRedirect: this.customUrlRedirect(),
            };

            const response: UseAuthorizationCodeOutput = await this._useAuthorizationCodeService.handle(input);

            this.handleSuccess(response);
        } catch (error) {
            this.handleError();
        }
    }

    private handleSuccess(response: UseAuthorizationCodeOutput): void {
        this._appHandler.setLoading(false);

        if (this.customUrlRedirect()) {
            const redirectUrl = response.customUrlRedirect;
            window.location.href = redirectUrl;
            return;
        }

        this._router.navigate([`/${this.cityIdentity()}`]);
    }

    private handleError(): void {
        this._appHandler.setLoading(false);
        this._appHandler.setError(true, {
            message: 'Erro ao processar autorização',
            description: 'Não foi possível validar suas credenciais. Tente novamente.',
            actionText: 'Tentar novamente',
            action: () => {
                this._appHandler.clearError();
                this.processAuthorizationCode();
            },
        });
    }
} 